import { FitAddon } from '@xterm/addon-fit';
import { SearchAddon } from '@xterm/addon-search';
import { WebLinksAddon } from '@xterm/addon-web-links';
import { WebglAddon } from '@xterm/addon-webgl';
import { Terminal } from '@xterm/xterm';
import { FleetNode, TerminalSnippet } from '../../shared/types';
import { iconSvg } from '../components/Icons';

interface MountedXtermPane {
  paneId: string;
  tabId: string;
  nodeId: string;
  title: string;
  broadcastSelected: boolean;
  webglActive: boolean;
  term: Terminal | null;
  fitAddon: FitAddon | null;
  searchAddon: SearchAddon | null;
  backlog: string[];
}

export class TerminalMuxWorkspace {
  private ws: WebSocket | null = null;
  private panes: MountedXtermPane[] = [];
  private tabs: Array<{ id: string; title: string; layout: 'layout-single' | 'layout-split-h' | 'layout-split-v' | 'layout-quad' }> = [
    { id: 'tab-cluster', title: 'Cluster & Edge Nodes', layout: 'layout-split-h' },
    { id: 'tab-hypervisor', title: 'Hypervisor & KVM Sandbox', layout: 'layout-split-h' }
  ];
  private activeTabId = 'tab-cluster';
  private activePaneId = 'pane-1';
  private liveBroadcastTyping = false;
  private regexQuery = '';
  private snippetModalOpen = false;
  private resizeObserver: ResizeObserver | null = null;

  private readonly getNodes: () => FleetNode[];
  private readonly getSnippets: () => TerminalSnippet[];
  private readonly onSaveSnippet: (snip: { title: string; category: TerminalSnippet['category']; description: string; commandTemplate: string }) => Promise<void>;
  private readonly onStatus: (msg: string) => void;

  constructor(opts: {
    getNodes: () => FleetNode[];
    getSnippets: () => TerminalSnippet[];
    onSaveSnippet: (snip: { title: string; category: TerminalSnippet['category']; description: string; commandTemplate: string }) => Promise<void>;
    onStatus: (msg: string) => void;
  }) {
    this.getNodes = opts.getNodes;
    this.getSnippets = opts.getSnippets;
    this.onSaveSnippet = opts.onSaveSnippet;
    this.onStatus = opts.onStatus;

    this.seedDefaultPanes();
  }

  private seedDefaultPanes(): void {
    this.panes = [
      {
        paneId: 'pane-1',
        tabId: 'tab-cluster',
        nodeId: 'ubuntu-pc',
        title: 'ubuntu-pc (192.168.88.229)',
        broadcastSelected: true,
        webglActive: false,
        term: null,
        fitAddon: null,
        searchAddon: null,
        backlog: []
      },
      {
        paneId: 'pane-2',
        tabId: 'tab-cluster',
        nodeId: 'orchestrator-core',
        title: 'orchestrator-core (192.168.88.67)',
        broadcastSelected: true,
        webglActive: false,
        term: null,
        fitAddon: null,
        searchAddon: null,
        backlog: []
      },
      {
        paneId: 'pane-3',
        tabId: 'tab-hypervisor',
        nodeId: 'pmox-ai-hypervisor',
        title: 'pmox-ai-hypervisor (192.168.88.2)',
        broadcastSelected: true,
        webglActive: false,
        term: null,
        fitAddon: null,
        searchAddon: null,
        backlog: []
      },
      {
        paneId: 'pane-4',
        tabId: 'tab-hypervisor',
        nodeId: 'sandbox-kvm-bios',
        title: 'sandbox-kvm-bios (127.0.0.1)',
        broadcastSelected: true,
        webglActive: false,
        term: null,
        fitAddon: null,
        searchAddon: null,
        backlog: []
      }
    ];
  }

  public ensureConnected(): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) return;

    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const token = localStorage.getItem('EA_KVM_AUTH_TOKEN') || '';
    const ws = new WebSocket(`${proto}//${window.location.host}/api/v1/terminal/ws?token=${encodeURIComponent(token)}`);
    this.ws = ws;

    ws.onopen = () => {
      for (const p of this.panes) {
        const cols = p.term?.cols || 100;
        const rows = p.term?.rows || 28;
        ws.send(JSON.stringify({ type: 'OPEN_PANE', paneId: p.paneId, nodeId: p.nodeId, cols, rows }));
      }
    };

    ws.onmessage = ev => {
      try {
        const msg = JSON.parse(String(ev.data)) as { type: string; paneId: string; data?: string };
        if (msg.type === 'OUTPUT' && msg.paneId && msg.data) {
          const pane = this.panes.find(x => x.paneId === msg.paneId);
          if (pane) {
            if (pane.term) {
              pane.term.write(msg.data);
            } else {
              pane.backlog.push(msg.data);
            }
          }
        }
      } catch {
        // Ignore invalid frame
      }
    };
  }

  public spawnPaneForNode(nodeId: string, container?: HTMLElement): void {
    this.ensureConnected();
    const nodes = this.getNodes();
    const node = nodes.find(n => n.id === nodeId);
    const paneId = `pane-${Date.now().toString().slice(-4)}`;
    const newPane: MountedXtermPane = {
      paneId,
      tabId: this.activeTabId,
      nodeId,
      title: `${node?.id || nodeId} (${node?.ip || '127.0.0.1'})`,
      broadcastSelected: true,
      webglActive: false,
      term: null,
      fitAddon: null,
      searchAddon: null,
      backlog: []
    };
    this.panes.push(newPane);
    this.activePaneId = paneId;

    const tabPanes = this.panes.filter(p => p.tabId === this.activeTabId);
    const activeTab = this.tabs.find(t => t.id === this.activeTabId);
    if (activeTab && tabPanes.length > 2) {
      activeTab.layout = 'layout-quad';
    }

    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type: 'OPEN_PANE', paneId, nodeId, cols: 100, rows: 28 }));
    }
    if (container) {
      this.render(container);
    }
  }

  public render(container: HTMLElement): void {
    this.ensureConnected();
    this.disposeMountedXterms();

    const nodes = this.getNodes();
    const snippets = this.getSnippets();
    const activeTab = this.tabs.find(t => t.id === this.activeTabId) || this.tabs[0];
    const visiblePanes = this.panes.filter(p => p.tabId === activeTab.id);

    container.innerHTML = `
      <div class="nx-term-workspace">
        <!-- Multi-Tab & Split-Pane Top Bar -->
        <div class="nx-filter-bar">
          <div class="nx-filter-group">
            ${this.tabs.map(t => `
              <button class="nx-btn ${t.id === activeTab.id ? 'nx-btn-primary' : ''} act-mux-tab" data-tab="${t.id}" type="button">
                ${iconSvg('terminal', 12)} ${this.escapeHtml(t.title)} (${this.panes.filter(p => p.tabId === t.id).length})
              </button>
            `).join('')}
            <button id="nx-add-mux-tab" class="nx-btn" type="button">${iconSvg('plus', 12)} Tab</button>

            <span style="color:#1E2430;">|</span>
            <button class="nx-btn ${activeTab.layout === 'layout-single' ? 'nx-btn-primary' : ''} act-term-layout" data-layout="layout-single" type="button">1x1</button>
            <button class="nx-btn ${activeTab.layout === 'layout-split-h' ? 'nx-btn-primary' : ''} act-term-layout" data-layout="layout-split-h" type="button">${iconSvg('split-h', 12)} Split H</button>
            <button class="nx-btn ${activeTab.layout === 'layout-split-v' ? 'nx-btn-primary' : ''} act-term-layout" data-layout="layout-split-v" type="button">${iconSvg('split-v', 12)} Split V</button>
            <button class="nx-btn ${activeTab.layout === 'layout-quad' ? 'nx-btn-primary' : ''} act-term-layout" data-layout="layout-quad" type="button">${iconSvg('layers', 12)} Quad 2x2</button>

            <select id="nx-term-spawn-node" class="nx-select mono">
              ${nodes.map(n => `<option value="${this.escapeHtml(n.id)}">+ Host: ${this.escapeHtml(n.name)}</option>`).join('')}
            </select>
            <button id="nx-btn-spawn-pane" class="nx-btn nx-btn-emerald" type="button">${iconSvg('plus', 12)} Spawn Pane</button>
          </div>

          <div class="nx-filter-group">
            <input id="nx-term-regex" class="nx-input mono" style="width:170px;" type="text" placeholder="Regex search buffer..." value="${this.escapeHtml(this.regexQuery)}" />
            <button id="nx-btn-search-next" class="nx-btn" type="button">${iconSvg('search', 12)} Next</button>

            <select id="nx-snippet-select" class="nx-select mono" style="width:210px;">
              ${snippets.map(s => `<option value="${this.escapeHtml(s.id)}">[${s.category}] ${this.escapeHtml(s.title)}</option>`).join('')}
            </select>
            <button id="nx-btn-run-snippet" class="nx-btn nx-btn-primary" type="button">${iconSvg('play', 12)} Run</button>
            <button id="nx-btn-new-snippet" class="nx-btn" title="Create Parameterized Snippet" type="button">${iconSvg('plus', 12)} Snippet</button>
          </div>
        </div>

        <!-- Multi-Exec Broadcast Input Bar -->
        <div style="display:flex;align-items:center;gap:8px;padding:6px 12px;background:#161A22;border-bottom:1px solid #1E2430;">
          <button id="nx-toggle-live-bcast" class="nx-btn ${this.liveBroadcastTyping ? 'nx-btn-emerald' : ''}" type="button">
            ${iconSvg('radio', 12)} ${this.liveBroadcastTyping ? 'LIVE KEYSTROKE BROADCAST: ON' : 'Live Keystroke Broadcast: OFF'}
          </button>
          <input id="nx-broadcast-input" class="nx-input mono" style="flex:1;" type="text" placeholder="Type command to broadcast simultaneously across all checked [BCAST] panes (Enter to execute)..." />
          <button id="nx-btn-broadcast-send" class="nx-btn nx-btn-emerald" type="button">Broadcast Exec</button>
        </div>

        <!-- Hardware-Accelerated xterm.js + WebGL Split-Pane Grid -->
        <div class="nx-term-grid ${activeTab.layout}">
          ${visiblePanes.map(p => `
            <div class="nx-term-pane ${p.paneId === this.activePaneId ? 'active-pane' : ''}" data-pane="${this.escapeHtml(p.paneId)}">
              <div class="nx-term-pane-header">
                <div style="display:flex;align-items:center;gap:8px;">
                  <label style="display:flex;align-items:center;gap:4px;cursor:pointer;">
                    <input class="act-pane-bcast" data-pane="${this.escapeHtml(p.paneId)}" type="checkbox" ${p.broadcastSelected ? 'checked' : ''} />
                    <span style="color:#10B981;font-weight:600;">BCAST</span>
                  </label>
                  <span style="color:#F3F4F6;font-weight:600;">${this.escapeHtml(p.title)}</span>
                  <span id="webgl-badge-${this.escapeHtml(p.paneId)}" class="nx-tag mono" style="color:#3B82F6;border-color:#1E2430;">WebGL</span>
                </div>
                <div style="display:flex;align-items:center;gap:4px;">
                  <button class="nx-btn act-clear-pane" data-pane="${this.escapeHtml(p.paneId)}" style="height:20px;padding:0 6px;font-size:10px;" type="button">CLR</button>
                  <button class="nx-btn act-close-pane" data-pane="${this.escapeHtml(p.paneId)}" style="height:20px;padding:0 6px;font-size:10px;" type="button">X</button>
                </div>
              </div>
              <div id="xterm-host-${this.escapeHtml(p.paneId)}" style="flex:1;width:100%;height:100%;padding:4px;background:#090B0E;overflow:hidden;"></div>
            </div>
          `).join('')}
        </div>
      </div>
      ${this.snippetModalOpen ? this.renderSnippetModal() : ''}
    `;

    this.mountXtermInstances(visiblePanes);
    this.bindEvents(container, activeTab);
  }

  private mountXtermInstances(visiblePanes: MountedXtermPane[]): void {
    this.resizeObserver = new ResizeObserver(() => {
      for (const p of visiblePanes) {
        if (p.fitAddon && p.term) {
          try {
            p.fitAddon.fit();
            if (this.ws && this.ws.readyState === WebSocket.OPEN) {
              this.ws.send(JSON.stringify({
                type: 'RESIZE',
                paneId: p.paneId,
                cols: p.term.cols,
                rows: p.term.rows
              }));
            }
          } catch {
            // Ignore resize transient error
          }
        }
      }
    });

    for (const p of visiblePanes) {
      const hostEl = document.getElementById(`xterm-host-${p.paneId}`);
      if (!hostEl) continue;

      const term = new Terminal({
        cursorBlink: true,
        cursorStyle: 'block',
        fontFamily: '"JetBrains Mono", "Fira Code", monospace',
        fontSize: 12.5,
        lineHeight: 1.22,
        scrollback: 5000,
        allowProposedApi: true,
        theme: {
          background: '#090B0E',
          foreground: '#F3F4F6',
          cursor: '#3B82F6',
          selectionBackground: 'rgba(59, 130, 246, 0.32)',
          black: '#090B0E',
          red: '#EF4444',
          green: '#10B981',
          yellow: '#F59E0B',
          blue: '#3B82F6',
          magenta: '#6366F1',
          cyan: '#06B6D4',
          white: '#F3F4F6',
          brightBlack: '#6B7280',
          brightRed: '#F87171',
          brightGreen: '#34D399',
          brightYellow: '#FBBF24',
          brightBlue: '#60A5FA',
          brightMagenta: '#818CF8',
          brightCyan: '#22D3EE',
          brightWhite: '#FFFFFF'
        }
      });

      const fitAddon = new FitAddon();
      const searchAddon = new SearchAddon();
      const webLinksAddon = new WebLinksAddon();

      term.loadAddon(fitAddon);
      term.loadAddon(searchAddon);
      term.loadAddon(webLinksAddon);
      term.open(hostEl);

      // Activate WebGL high-performance renderer with safe fallback
      try {
        const webglAddon = new WebglAddon();
        webglAddon.onContextLoss(() => {
          webglAddon.dispose();
        });
        term.loadAddon(webglAddon);
        p.webglActive = true;
      } catch {
        p.webglActive = false;
        const badge = document.getElementById(`webgl-badge-${p.paneId}`);
        if (badge) badge.textContent = 'Canvas2D';
      }

      try {
        fitAddon.fit();
      } catch {
        // Ignore initial fit
      }

      if (p.backlog.length > 0) {
        term.write(p.backlog.join(''));
        p.backlog = [];
      }

      term.onData(data => {
        this.activePaneId = p.paneId;
        if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
        if (this.liveBroadcastTyping && p.broadcastSelected) {
          const targetIds = this.panes.filter(x => x.tabId === this.activeTabId && x.broadcastSelected).map(x => x.paneId);
          this.ws.send(JSON.stringify({ type: 'BROADCAST_INPUT', paneIds: targetIds, data }));
        } else {
          this.ws.send(JSON.stringify({ type: 'INPUT', paneId: p.paneId, data }));
        }
      });

      p.term = term;
      p.fitAddon = fitAddon;
      p.searchAddon = searchAddon;
      this.resizeObserver.observe(hostEl);
    }
  }

  private renderSnippetModal(): string {
    return `
      <div class="nx-modal-backdrop">
        <div class="nx-cmd-modal" style="width:520px;padding:14px;">
          <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:10px;">
            <span style="font-weight:600;">CREATE PARAMETERIZED SNIPPET</span>
            <button id="nx-close-snip-modal" class="nx-btn" type="button">${iconSvg('x', 12)}</button>
          </div>
          <div style="display:flex;flex-direction:column;gap:8px;">
            <input id="snip-title" class="nx-input" type="text" placeholder="Snippet Title (e.g. ZFS Pool & SMART Triage)" />
            <select id="snip-cat" class="nx-select mono">
              <option value="DIAGNOSTIC">Category: DIAGNOSTIC</option>
              <option value="NETWORK">Category: NETWORK</option>
              <option value="SYSTEMD">Category: SYSTEMD</option>
              <option value="SECURITY">Category: SECURITY</option>
              <option value="STORAGE">Category: STORAGE</option>
            </select>
            <input id="snip-desc" class="nx-input" type="text" placeholder="Short technical description..." />
            <textarea id="snip-cmd" class="nx-input mono" style="height:80px;padding:8px;" placeholder="Command using {{hostname}}, {{ip}}, {{port}}, {{user}}...">echo "Checking {{hostname}} ({{ip}})" && uptime</textarea>
            <button id="nx-save-snip-btn" class="nx-btn nx-btn-primary" type="button">${iconSvg('check', 12)} Save Snippet</button>
          </div>
        </div>
      </div>
    `;
  }

  private bindEvents(container: HTMLElement, activeTab: { id: string; layout: 'layout-single' | 'layout-split-h' | 'layout-split-v' | 'layout-quad' }): void {
    container.querySelectorAll('.act-mux-tab').forEach(btn => {
      btn.addEventListener('click', () => {
        this.activeTabId = (btn as HTMLElement).dataset.tab || 'tab-cluster';
        this.render(container);
      });
    });

    container.querySelector('#nx-add-mux-tab')?.addEventListener('click', () => {
      const id = `tab-${Date.now().toString().slice(-3)}`;
      this.tabs.push({ id, title: `Workspace ${this.tabs.length + 1}`, layout: 'layout-single' });
      this.activeTabId = id;
      this.spawnPaneForNode('ubuntu-pc', container);
    });

    container.querySelectorAll('.act-term-layout').forEach(btn => {
      btn.addEventListener('click', () => {
        activeTab.layout = ((btn as HTMLElement).dataset.layout as typeof activeTab.layout) || 'layout-split-h';
        this.render(container);
      });
    });

    container.querySelector('#nx-btn-spawn-pane')?.addEventListener('click', () => {
      const sel = container.querySelector('#nx-term-spawn-node') as HTMLSelectElement;
      this.spawnPaneForNode(sel.value, container);
    });

    const runRegexSearch = () => {
      const q = (container.querySelector('#nx-term-regex') as HTMLInputElement).value;
      this.regexQuery = q;
      if (!q) return;
      for (const p of this.panes.filter(x => x.tabId === this.activeTabId)) {
        p.searchAddon?.findNext(q, { regex: true, caseSensitive: false });
      }
    };

    container.querySelector('#nx-btn-search-next')?.addEventListener('click', runRegexSearch);
    container.querySelector('#nx-term-regex')?.addEventListener('keydown', e => {
      if ((e as KeyboardEvent).key === 'Enter') runRegexSearch();
    });

    container.querySelector('#nx-btn-run-snippet')?.addEventListener('click', () => {
      const snipId = (container.querySelector('#nx-snippet-select') as HTMLSelectElement).value;
      const paneIds = this.panes.filter(p => p.tabId === this.activeTabId && p.broadcastSelected).map(p => p.paneId);
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ type: 'EXEC_SNIPPET', snippetId: snipId, paneIds }));
        this.onStatus(`Executed snippet '${snipId}' across ${paneIds.length} WebGL terminal panes.`);
      }
    });

    container.querySelector('#nx-btn-new-snippet')?.addEventListener('click', () => {
      this.snippetModalOpen = true;
      this.render(container);
    });

    container.querySelector('#nx-close-snip-modal')?.addEventListener('click', () => {
      this.snippetModalOpen = false;
      this.render(container);
    });

    container.querySelector('#nx-save-snip-btn')?.addEventListener('click', async () => {
      const title = (container.querySelector('#snip-title') as HTMLInputElement).value.trim() || 'Custom Snippet';
      const category = (container.querySelector('#snip-cat') as HTMLSelectElement).value as TerminalSnippet['category'];
      const description = (container.querySelector('#snip-desc') as HTMLInputElement).value.trim();
      const commandTemplate = (container.querySelector('#snip-cmd') as HTMLTextAreaElement).value.trim() || 'uptime';
      await this.onSaveSnippet({ title, category, description, commandTemplate });
      this.snippetModalOpen = false;
      this.render(container);
    });

    container.querySelector('#nx-toggle-live-bcast')?.addEventListener('click', () => {
      this.liveBroadcastTyping = !this.liveBroadcastTyping;
      this.render(container);
    });

    const sendBcastBar = () => {
      const inp = container.querySelector('#nx-broadcast-input') as HTMLInputElement;
      const cmd = inp.value;
      if (!cmd.trim()) return;
      const paneIds = this.panes.filter(p => p.tabId === this.activeTabId && p.broadcastSelected).map(p => p.paneId);
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ type: 'BROADCAST_INPUT', paneIds, data: cmd + '\r' }));
      }
      inp.value = '';
      this.onStatus(`Broadcasted command to ${paneIds.length} active panes.`);
    };

    container.querySelector('#nx-btn-broadcast-send')?.addEventListener('click', sendBcastBar);
    container.querySelector('#nx-broadcast-input')?.addEventListener('keydown', e => {
      if ((e as KeyboardEvent).key === 'Enter') sendBcastBar();
    });

    container.querySelectorAll('.act-pane-bcast').forEach(chk => {
      chk.addEventListener('change', () => {
        const pid = (chk as HTMLElement).dataset.pane;
        const pane = this.panes.find(x => x.paneId === pid);
        if (pane) pane.broadcastSelected = (chk as HTMLInputElement).checked;
      });
    });

    container.querySelectorAll('.act-clear-pane').forEach(btn => {
      btn.addEventListener('click', () => {
        const pid = (btn as HTMLElement).dataset.pane;
        const pane = this.panes.find(x => x.paneId === pid);
        pane?.term?.clear();
      });
    });

    container.querySelectorAll('.act-close-pane').forEach(btn => {
      btn.addEventListener('click', () => {
        const pid = (btn as HTMLElement).dataset.pane || '';
        const visible = this.panes.filter(p => p.tabId === this.activeTabId);
        if (visible.length <= 1) return;
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
          this.ws.send(JSON.stringify({ type: 'CLOSE_PANE', paneId: pid }));
        }
        const target = this.panes.find(p => p.paneId === pid);
        target?.term?.dispose();
        this.panes = this.panes.filter(p => p.paneId !== pid);
        this.render(container);
      });
    });
  }

  public disposeMountedXterms(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    for (const p of this.panes) {
      if (p.term) {
        try {
          p.term.dispose();
        } catch {
          // Ignore
        }
        p.term = null;
        p.fitAddon = null;
        p.searchAddon = null;
      }
    }
  }

  private escapeHtml(s: string): string {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
}
